/**
 * HORO-10459: single source of truth for whether GA4 DebugView should be on.
 *
 * The original gate was `process.env.NODE_ENV !== 'production'`, which only ever
 * fires on a local dev server: `react-scripts build` hard-codes
 * `process.env.NODE_ENV = 'production'` (build.js) before CRA even reads the
 * `.env` files, so EVERY deployed build — dev and stage included — had the
 * branch folded away by the minifier. Events on veda-dev were being sent but
 * were invisible in DebugView.
 *
 * The fix keys off `REACT_APP_ENVIRONMENT` instead, which is already set per
 * environment ('local' / 'dev' / 'prod') and, unlike NODE_ENV, is a real
 * build-time input we control. No new environment variable needed.
 *
 * Debug is on everywhere EXCEPT production, plus a per-session escape hatch:
 *
 *   - `?ga_debug=1`, or `localStorage.ga_debug === '1'` — works ANYWHERE,
 *     production included, since it takes a deliberate action and is the only
 *     way to verify a prod release without a rebuild. Mirrors the existing
 *     `bc_debug` flag on the birth-chart widget.
 *
 * Note the direction of the production check: it excludes prod explicitly rather
 * than allow-listing known non-prod values, so a new environment name doesn't
 * silently lose DebugView. That leans on `REACT_APP_ENVIRONMENT` being correct in
 * the prod secret — which it must be regardless, because canonical URLs and
 * robots meta already branch on it (useSeoTags), as does error-message verbosity
 * in the chat pages. A wrong value there would be visible long before it
 * mattered here.
 */
import { APP_ENVIRONMENT } from '../const';

const IS_LOCAL_BUILD = process.env.NODE_ENV !== 'production';
const IS_PRODUCTION_ENV = APP_ENVIRONMENT === 'production' || APP_ENVIRONMENT === 'prod';

export const QUERY_PARAM_GA_DEBUG = 'ga_debug';
export const STORAGE_KEY_GA_DEBUG = 'ga_debug';

// Captured once at module load: `init()` runs before any navigation, so reading
// the query string later would miss it, and a value that flipped mid-session
// would split one session across debug and non-debug events.
const _initialGaDebugOptIn = (() => {
    if (typeof window === 'undefined') return false;
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get(QUERY_PARAM_GA_DEBUG) === '1') return true;
    } catch {
        // Malformed URL — fall through to storage.
    }
    try {
        return window.localStorage.getItem(STORAGE_KEY_GA_DEBUG) === '1';
    } catch {
        // Sandboxed iframe / storage blocked.
        return false;
    }
})();

const isGaDebugEnabled = () => _initialGaDebugOptIn || IS_LOCAL_BUILD || !IS_PRODUCTION_ENV;

export default isGaDebugEnabled;
